import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { initTheme } from "@/lib/theme";
import { setSeo } from "@/lib/seo";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { MarketingNav } from "@/components/MarketingNav";
import { MarketingFooter } from "@/components/MarketingFooter";
import { Eyebrow } from "@/components/Eyebrow";
import { ArrowRight, GraduationCap, Award, Landmark, Users } from "lucide-react";

const CREDENTIALS = [
  { icon: GraduationCap, text: "MFA, Brand Development & Internet Marketing — Valedictorian, Full Sail University" },
  { icon: Award, text: "Executive Certificates in Human-Computer Interaction & Extended Reality — MIT" },
  { icon: Landmark, text: "Executive Education — Dartmouth's Tuck School of Business" },
  { icon: Users, text: "Advisory Board — UC Berkeley Extension, UX Design program" },
];

const CLIENTS = ["FedEx", "PNC", "Rosetta Stone", "K12", "Center for Medicare & Medicaid", "University of Pittsburgh", "Carnegie Mellon", "U.S. Department of Education", "Pfizer", "Merck", "GSK", "National PTA"];

export default function AboutUs() {
  const navigate = useNavigate();
  useScrollReveal();
  useEffect(() => {
    initTheme();
    setSeo({
      title: "About HeyDesignGuy — Built by a brand architect, for brand builders",
      description: "HeyDesignGuy is a creative and brand strategy consultancy founded in 2001 with 25+ years building brands for FedEx, Pfizer, Carnegie Mellon and more. HDG Creative Studio brings agency-caliber brand thinking to everyone, powered by AI.",
      canonical: "https://creativestudio.demosforclients.com/about-us",
    });
  }, []);

  return (
    <div className="lightboard" data-testid="about-page" style={{ minHeight: "100vh" }}>
      <MarketingNav />

      {/* ---------- HERO ---------- */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "84px 22px 48px", textAlign: "center" }}>
        <div className="mkt-fade-up in-view">
          <Eyebrow className="!justify-center" testId="about-eyebrow">About HeyDesignGuy</Eyebrow>
          <h1 className="font-display" style={{ fontSize: "clamp(38px, 6vw, 60px)", fontWeight: 800, letterSpacing: "-0.035em", lineHeight: 1.04, color: "var(--tprimary)", marginTop: 22 }}>
            Built by a brand architect,<br /><span style={{ color: "var(--orange)" }}>for brand builders.</span>
          </h1>
        </div>
      </section>

      {/* ---------- FOUNDER ---------- */}
      <section style={{ maxWidth: 1080, margin: "0 auto", padding: "16px 22px 60px" }}>
        <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-11 items-start">
          <div className="mkt-fade-up">
            <div className="panel-card" style={{ padding: 12, overflow: "hidden" }}>
              <img src="/marketing/about.png" alt="Architectural drafting aesthetic representing brand craft" style={{ width: "100%", borderRadius: 10, display: "block" }} />
            </div>
            <div className="font-mono" style={{ fontSize: 12, color: "var(--orange)", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 18, textAlign: "center" }}>
              Educator. Challenger. Architect.
            </div>
          </div>

          <div className="mkt-fade-up" style={{ animationDelay: "0.1s" }}>
            <Eyebrow testId="founder-eyebrow">The founder</Eyebrow>
            <div style={{ display: "flex", flexDirection: "column", gap: 18, marginTop: 18 }}>
              <p className="font-body" style={{ fontSize: 16.5, color: "var(--tsecondary)", lineHeight: 1.7 }}>
                For over 25 years, HeyDesignGuy has built brand and digital experiences for some of the most recognized organizations in the world — FedEx, PNC, Rosetta Stone, K12, Center for Medicare &amp; Medicaid, University of Pittsburgh, Carnegie Mellon, the U.S. Department of Education, Pfizer, Merck, GSK, and the National PTA, among them.
              </p>
              <p className="font-body" style={{ fontSize: 16.5, color: "var(--tsecondary)", lineHeight: 1.7 }}>
                Founded in 2001, HeyDesignGuy is a creative and brand strategy consultancy built on a simple belief: every business deserves agency-caliber thinking, not just agencies with the biggest budgets.
              </p>
              <p className="font-body" style={{ fontSize: 16.5, color: "var(--tsecondary)", lineHeight: 1.7 }}>
                Its creator and founder holds an MFA in Brand Development and Internet Marketing (Valedictorian) from Full Sail University, Executive Certificates in Human-Computer Interaction and Extended Reality from MIT, and an executive education credential from Dartmouth's Tuck School of Business. He serves on the Advisory Board for UC Berkeley Extension's UX Design program and continues to build under the framework that defines his work: <span style={{ color: "var(--tprimary)", fontWeight: 600 }}>Educator. Challenger. Architect.</span>
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 28 }}>
              {CREDENTIALS.map((c, i) => {
                const Icon = c.icon;
                return (
                  <div key={i} data-testid={`credential-${i + 1}`} className="panel-card" style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(249,115,22,0.1)", border: "1px solid var(--cardhover)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon size={18} style={{ color: "var(--orange)" }} />
                    </div>
                    <span className="font-body" style={{ fontSize: 13.5, color: "var(--tsecondary)", lineHeight: 1.5 }}>{c.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- CLIENT STRIP ---------- */}
      <section style={{ maxWidth: 1000, margin: "0 auto", padding: "8px 22px 60px" }}>
        <div className="mkt-fade-up" style={{ textAlign: "center" }}>
          <p className="font-mono" style={{ fontSize: 11, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Trusted by the brands behind</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px 30px", justifyContent: "center", marginTop: 20 }}>
            {CLIENTS.map((c) => (
              <span key={c} className="font-display" style={{ fontSize: 15, fontWeight: 700, color: "var(--tsecondary)", opacity: 0.65 }}>{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- MISSION ---------- */}
      <section style={{ maxWidth: 820, margin: "0 auto", padding: "30px 22px 64px" }}>
        <div className="mkt-fade-up panel-card" style={{ padding: "40px 34px" }}>
          <Eyebrow testId="mission-eyebrow">Our mission</Eyebrow>
          <p className="font-body" style={{ fontSize: 17, color: "var(--tprimary)", lineHeight: 1.7, marginTop: 18 }}>
            HDG Creative Studio exists because brand strategy shouldn't be locked behind agency retainers. We built this tool to give small businesses, solo operators, and growing agencies the same caliber of brand-aware creative output that used to require a full creative team — powered by AI, grounded in two and a half decades of real brand strategy experience.
          </p>
          <p className="font-display" style={{ fontSize: 20, fontWeight: 700, color: "var(--orange)", lineHeight: 1.4, marginTop: 22, letterSpacing: "-0.01em" }}>
            Paste a URL. Watch your brand come to life. That's the whole idea.
          </p>
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "10px 22px 84px" }}>
        <div className="mkt-fade-up" style={{ textAlign: "center" }}>
          <h2 className="font-display" style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 800, letterSpacing: "-0.03em", color: "var(--tprimary)" }}>
            Ready to see your brand in action?
          </h2>
          <button data-testid="about-cta-button" onClick={() => navigate("/start")} className="btn-primary" style={{ marginTop: 26, padding: "14px 30px", fontSize: 16 }}>
            Start Free <ArrowRight size={17} />
          </button>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
